import { useCallback, useEffect, useState } from 'react';
import type { Api, AuditEvent } from '../api';
import type { T } from '../i18n';
import { errorText } from './AgentSettings';

/** A readable name for an action: a known one by its key, otherwise the area of the route. */
const actionText = (t: T, action: string): string => {
  const key = `audit.action.${action}`;
  if (t(key) !== key) return t(key);
  const area = /\/v2\/api\/([a-z-]+)/u.exec(action)?.[1] ?? 'other';
  const verb = action.split(' ')[0] ?? '';
  const a = t(`audit.area.${area}`) === `audit.area.${area}` ? area : t(`audit.area.${area}`);
  return `${a} · ${t(`audit.verb.${verb}`) === `audit.verb.${verb}` ? verb : t(`audit.verb.${verb}`)}`;
};

/** ADR-071 (BRAIN/31 B4): who did what in the panel — changes, recordings listened to, refused attempts. */
export const AuditPage = ({ api, t }: { api: Api; t: T }) => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string>();

  const load = useCallback(
    (before?: number) =>
      api
        .audit(before)
        .then((r) => {
          setEvents((prev) => (before ? [...prev, ...r.events] : r.events));
          setMore(r.events.length === 100);
        })
        .catch((e: unknown) => setError(errorText(t, e))),
    [api, t],
  );
  useEffect(() => void load(), [load]);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1>{t('audit.title')}</h1>
          <p className="muted">{t('audit.subtitle')}</p>
        </div>
      </header>
      {error && <div className="banner error small">{error}</div>}
      {!error && events.length === 0 && <div className="empty"><p>{t('audit.empty')}</p></div>}
      {events.length > 0 && (
        <table className="table">
          <thead>
            <tr><th>{t('audit.when')}</th><th>{t('audit.who')}</th><th>{t('audit.what')}</th><th>{t('audit.on')}</th><th>{t('audit.result')}</th></tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td className="nowrap">{new Date(e.createdAt).toLocaleString()}</td>
                <td>{e.actorName ?? e.actor}</td>
                <td>{actionText(t, e.action)}</td>
                <td className="mono hint">{Object.values(e.target).join(' · ')}</td>
                <td><span className={`pill ${e.result === 'ok' ? 'ok' : e.result === 'denied' ? 'warn' : 'error'}`}>{t(`audit.res.${e.result}`)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {more && <button type="button" className="ghost section-gap" onClick={() => void load(events.at(-1)?.id)}>{t('audit.more')}</button>}
    </section>
  );
};
